import { SxProps, Theme } from '@mui/material/styles';
import {
  RADIUS,
  brandColors,
  APP_FONT_FAMILY,
  TYPE,
  INPUT_HEIGHT,
  INPUT_HEIGHT_DENSE,
  INPUT_HEIGHT_COMPACT,
  INPUT_HEIGHT_AUTH,
  INPUT_PX,
} from './appTheme';
import { brandCss } from './brandCss';

/** Label sits above the box (outside the field) — used system-wide */
export const externalLabelSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  '& .MuiInputLabel-root': {
    position: 'relative !important',
    transform: 'none !important',
    translate: 'none',
    marginBottom: '6px',
    marginLeft: 0,
    marginTop: 0,
    padding: 0,
    maxWidth: '100%',
    fontSize: TYPE.fieldLabel.fontSize,
    fontFamily: APP_FONT_FAMILY,
    fontWeight: TYPE.fieldLabel.fontWeight,
    lineHeight: TYPE.fieldLabel.lineHeight,
    color: brandColors.textDark,
    pointerEvents: 'auto',
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: brandCss.primaryDark,
  },
  '& .MuiInputLabel-asterisk': {
    color: brandColors.danger,
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    width: '0 !important',
    maxWidth: 0,
    padding: 0,
    '& span': { display: 'none' },
  },
  '& .MuiInputLabel-shrink': {
    transform: 'none !important',
  },
};

/** CRM form fields — 44px height */
export const catchyTextFieldSx: SxProps<Theme> = {
  ...externalLabelSx,
  fontFamily: APP_FONT_FAMILY,
  '& .MuiOutlinedInput-root': {
    borderRadius: `${RADIUS}px`,
    bgcolor: '#fff',
    fontSize: TYPE.fieldInput.fontSize,
    fontFamily: APP_FONT_FAMILY,
    fontWeight: TYPE.fieldInput.fontWeight,
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '& input, & .MuiSelect-select': {
      fontSize: TYPE.fieldInput.fontSize,
      fontFamily: APP_FONT_FAMILY,
      fontWeight: TYPE.fieldInput.fontWeight,
      lineHeight: TYPE.fieldInput.lineHeight,
      py: 0,
      height: INPUT_HEIGHT,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      px: `${INPUT_PX}px`,
      '&::placeholder': {
        fontSize: TYPE.fieldInput.fontSize,
        opacity: 0.7,
      },
    },
    '& textarea': {
      fontSize: TYPE.fieldInput.fontSize,
      fontFamily: APP_FONT_FAMILY,
      py: 1.25,
      px: `${INPUT_PX}px`,
      lineHeight: 1.5,
    },
    '& fieldset': {
      borderColor: brandColors.border,
      borderWidth: 1.5,
    },
    '&:hover fieldset': {
      borderColor: brandCss.primaryLight,
    },
    '&.Mui-focused': {
      boxShadow: brandCss.focusRing,
      '& fieldset': {
        borderColor: brandCss.primary,
        borderWidth: 2,
      },
    },
    '&.Mui-error fieldset': {
      borderColor: brandColors.danger,
    },
    '&.Mui-disabled': {
      bgcolor: '#f8fafc',
    },
  },
  '& .MuiFormHelperText-root': {
    fontSize: TYPE.helper.fontSize,
    fontFamily: APP_FONT_FAMILY,
    fontWeight: TYPE.helper.fontWeight,
    marginLeft: 2,
    marginTop: 6,
    lineHeight: TYPE.helper.lineHeight,
  },
};

/** Dense toolbar / filter fields — 32px, defeats global external-label + 44px theme */
export const toolbarTextFieldSx: SxProps<Theme> = {
  fontFamily: APP_FONT_FAMILY,
  margin: '0 !important',
  padding: '0 !important',
  verticalAlign: 'middle',
  display: 'inline-flex !important',
  flexDirection: 'row !important',
  alignItems: 'center',
  alignSelf: 'center',
  boxSizing: 'border-box',
  // Kill theme external label (relative + marginBottom) that causes top gap
  '& .MuiInputLabel-root': {
    display: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
    height: '0 !important',
    minHeight: '0 !important',
    position: 'absolute !important',
    transform: 'none !important',
  },
  '& .MuiFormHelperText-root': {
    display: 'none !important',
  },
  '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
    margin: 0,
    borderRadius: `${RADIUS}px`,
    bgcolor: '#fff',
    height: `${INPUT_HEIGHT_COMPACT}px !important`,
    minHeight: `${INPUT_HEIGHT_COMPACT}px !important`,
    maxHeight: `${INPUT_HEIGHT_COMPACT}px !important`,
    boxSizing: 'border-box',
    fontSize: '12px !important',
    fontFamily: APP_FONT_FAMILY,
    fontWeight: 500,
    alignItems: 'center',
    padding: '0 !important',
    boxShadow: 'none !important',
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderColor: `${brandColors.border} !important`,
      borderWidth: '1px !important',
      top: '0 !important',
      legend: { display: 'none', width: 0, padding: 0 },
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${brandColors.border} !important`,
      borderWidth: '1px !important',
    },
    '&.Mui-focused': {
      boxShadow: 'none !important',
      '& fieldset, & .MuiOutlinedInput-notchedOutline': {
        borderColor: `${brandCss.primary} !important`,
        borderWidth: '1px !important',
      },
    },
  },
  // Override MUI sizeSmall select padding (8.5px top) that misaligns text
  '& .MuiOutlinedInput-input, & .MuiInputBase-input, & .MuiSelect-select': {
    height: `${INPUT_HEIGHT_COMPACT}px !important`,
    minHeight: `${INPUT_HEIGHT_COMPACT}px !important`,
    maxHeight: `${INPUT_HEIGHT_COMPACT}px !important`,
    boxSizing: 'border-box',
    fontSize: '12px !important',
    fontFamily: APP_FONT_FAMILY,
    fontWeight: 500,
    lineHeight: `${INPUT_HEIGHT_COMPACT}px !important`,
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '10px !important',
    paddingRight: '10px !important',
    display: 'flex',
    alignItems: 'center',
    '&::placeholder': {
      fontSize: 12,
      opacity: 0.7,
    },
  },
  '& .MuiSelect-select': {
    paddingRight: '28px !important',
  },
  '& .MuiSelect-icon': {
    fontSize: 18,
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiInputAdornment-root': {
    margin: 0,
    height: `${INPUT_HEIGHT_COMPACT}px`,
    maxHeight: `${INPUT_HEIGHT_COMPACT}px`,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: 16,
  },
};

/** Compact outlined action buttons in table toolbar */
export const tableActionButtonSx = {
  height: INPUT_HEIGHT_COMPACT,
  minHeight: INPUT_HEIGHT_COMPACT,
  px: 1.25,
  borderRadius: `${RADIUS}px`,
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: '16px',
  fontFamily: APP_FONT_FAMILY,
  whiteSpace: 'nowrap' as const,
};

export const statusFieldHighlightSx: SxProps<Theme> = {
  ...catchyTextFieldSx,
  '& .MuiOutlinedInput-root': {
    ...(catchyTextFieldSx as Record<string, unknown>)['& .MuiOutlinedInput-root'] as object,
    bgcolor: brandCss.green50,
    '&.Mui-focused': {
      boxShadow: brandCss.focusRingStrong,
    },
  },
};

export const catchyButtonSx = {
  borderRadius: `${RADIUS}px`,
  fontWeight: TYPE.button.fontWeight,
  fontSize: TYPE.button.fontSize,
  lineHeight: TYPE.button.lineHeight,
  fontFamily: APP_FONT_FAMILY,
  textTransform: 'none' as const,
  minHeight: INPUT_HEIGHT,
};

export const catchyPrimaryButtonSx = {
  ...catchyButtonSx,
  color: '#fff',
  background: brandCss.gradientPrimary,
  boxShadow: brandCss.shadowButton,
  '&:hover': {
    background: brandCss.gradientPrimaryHover,
    color: '#fff',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.65)',
  },
};

/** Auth primary CTA — 48px */
export const authPrimaryButtonSx = {
  ...catchyPrimaryButtonSx,
  minHeight: INPUT_HEIGHT_AUTH,
  py: 1.5,
  fontSize: 15,
  fontWeight: 700,
  borderRadius: 2,
};

export const catchyPaperSx = {
  borderRadius: `${RADIUS}px`,
  border: `1px solid ${brandColors.border}`,
  boxShadow: brandCss.shadowMd,
};

/** Keep dense height export for dashboard filter bar */
export { INPUT_HEIGHT_DENSE };
